<template>
  <div class="scene-shape-menu">
    <item-header
      :label="$t('objectNames.shape')"
      @close="$emit('close')"
    >
      <template #content>
        <editable-item-title :item="sceneNode" />
      </template>
    </item-header>

    <drawer-block
      :title="$t('labels.design')"
      v-model:open="drawerStates.shapeDesign"
    >
      <div class="column gap-4">
        <div>
          <h3 class="label mv-0">
            {{ $t("headings.colors") }}
          </h3>
          <div class="line gap-4">
            <tooltip-button
              class="scene-shape-menu__button"
              :title="$t('labels.fill')"
              placement="bottom"
            >
              <div class="line gap-2">
                <FillSymbol :fills="[sceneNode.fill]" />
                <label class="label mv-0">{{ $t("labels.fill") }}</label>
              </div>

              <template #tooltip>
                <div class="p-2">
                  <color-picker
                    id="fillColor"
                    :value="sceneNode.fill"
                    @update:value="sceneNodeStore.updateKey(sceneNode.id, 'fill', $event || '#00000000')"
                  />
                </div>
              </template>
            </tooltip-button>

            <tooltip-button
              class="scene-shape-menu__button"
              :title="$t('labels.stroke')"
              placement="bottom"
              :style="{ backgroundColor: strokeContrastColor }"
            >
              <div class="line gap-2">
                <svg
                  style="flex-shrink: 0"
                  width="18"
                  height="18"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="2" y="2" width="14" height="2" :fill="sceneNode.stroke ?? undefined" />
                  <rect x="2" y="6" width="14" height="3" :fill="sceneNode.stroke ?? undefined" />
                  <rect x="2" y="11" width="14" height="4" :fill="sceneNode.stroke ?? undefined" />
                </svg>
                <label class="label mv-0">{{ $t("labels.stroke") }}</label>
              </div>

              <template #tooltip>
                <div class="p-2">
                  <color-picker
                    :value="sceneNode.stroke"
                    :null-allowed="true"
                    @update:value="sceneNodeStore.updateKey(sceneNode.id, 'stroke', $event || '#00000000')"
                  />
                </div>
              </template>
            </tooltip-button>
          </div>
        </div>

        <div>
          <label class="label" for="fillOpacity">
            {{ $t("labels.opacity") }}
          </label>
          <input-range
            id="fillOpacity"
            :value="fillOpacity"
            :min="0"
            :max="100"
            :step="1"
            :colors="[fillFullOpacity]"
            :style="{
              filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.2))'
            }"
            @update:value="fillOpacity = $event"
          />
        </div>

        <div>
          <label class="label" for="strokeWidth">
            {{ $t("labels.strokeWidth") }}
          </label>
          <input-range
            id="strokeWidth"
            :value="sceneNode.strokeWidth ?? 1"
            :min="0"
            :max="10"
            :step="1"
            @update:value="sceneNodeStore.updateKey(sceneNode.id, 'strokeWidth', $event)"
          />
        </div>

        <div class="column gap-1 mt-4">
          <input-switch
            :value="sceneNode.visible"
            @update:value="sceneNodeStore.updateKey(sceneNode.id, 'visible', $event)"
          >
            {{ $t("labels.visible") }}
          </input-switch>
        </div>
      </div>
    </drawer-block>

    <menu-group class="mt-auto">
      <button
        type="button"
        class="button button_secondary mv-2"
        @click="sceneNodeStore.delete(sceneNode)"
      >
        {{ $t("actions.deleteShape", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getContrastColor, toHSLA } from "@/helpers/utility/Color";

import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import ItemHeader from "@editor/components/ItemHeader.vue";
import TooltipButton from "@core/ui/TooltipButton.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import FillSymbol from "@core/ui/FillSymbol.vue";
import ColorPicker from "@core/input/ColorPicker.vue";
import InputRange from "@core/input/InputRange.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import type { Uid } from "@/helpers/utility/Id";

interface Props {
  nodeUid: Uid<"sceneNode">;
}
const props = defineProps<Props>();

defineEmits<{
  (event: "close"): void;
}>();

const sceneNodeStore = useSceneNodesStore();
const drawerStates = useDrawerStates();

const sceneNode = computed(() => sceneNodeStore.byUid[props.nodeUid]);

const fillOpacity = computed({
  get() {
    const fillColor = sceneNode.value.fill;
    if (fillColor == null) return 100;
    const fillHSLA = toHSLA(fillColor);
    return fillHSLA.a * 100;
  },
  set(value: number) {
    const fillColor = sceneNode.value.fill;
    const fillHSLA = fillColor ? toHSLA(fillColor) : { h: 0, s: 0, l: 0, a: 1 };
    const hue = fillHSLA.h;
    const saturation = fillHSLA.s;
    const lightness = fillHSLA.l;
    const newOpacity = value / 100;
    const newFillColor = `hsla(${hue}, ${saturation * 100}%, ${lightness * 100}%, ${newOpacity})`;

    sceneNodeStore.updateKey(sceneNode.value.id, "fill", newFillColor);
  }
});

const fillFullOpacity = computed(() => {
  // get the fill color with full opacity
  const fillColor = sceneNode.value.fill || "#00000000";
  const fillHSLA = toHSLA(fillColor);
  return `hsla(${fillHSLA.h}, ${fillHSLA.s * 100}%, ${fillHSLA.l * 100}%, 1)`;
});


const strokeContrastColor = computed(() => {
  // Calculate a contrasting color for the stroke
  const strokeColor = sceneNode.value.stroke || "#00000000";
  return getContrastColor(strokeColor);
});
</script>

<style lang="scss">
.scene-shape-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;

  &__button {
    background-color: var(--color-background-close);
    padding: 0.5rem 0.75rem;
    border: none;
    box-shadow: 0 1px 4px var(--color-shadow-2);
    border-radius: 0.25rem;
    transition: background-color 0.2s ease-out, box-shadow 0.2s ease-out;

    &:hover {
      background-color: var(--color-background-close-hover);
      box-shadow: 0 1px 5px var(--color-shadow-3);
    }
  }
}
</style>